import * as THREE from "three";
import { VFX } from "../../../../art/vfx.js";
import { AnimRig } from "./rig";
import { lib } from "./views/common";
import { burstTime, lidOpensUp, tintLidInside } from "./chestLook";
import { ChestShow, findPart } from "./chestShow";

/**
 * One reward chest on the stage (chestStage.ts): the `chest_<rarity>` model at the stage's scale, its clips, the lid
 * (and its lit lining) and its show (chestShow.ts: sigil, pillar, fountain…). The rarity climb swaps one for the next
 * tier's mid-reveal, so everything that belongs to one rarity lives here and goes with `dispose`.
 */
export class ChestModel {
  readonly root: THREE.Object3D;
  readonly rig: AnimRig;
  readonly show: ChestShow;
  readonly lid: THREE.Object3D | null;
  readonly rank: number;
  readonly colour: [number, number, number];
  /** When the burst lands in the `open` clip (s). */
  readonly burstAt: number;
  /** The rank colour the lid's inside glows with after the burst (chestLook.ts `tintLidInside`); 0 until the burst. */
  readonly lidGlow = { value: new THREE.Color(0, 0, 0) };

  static readonly Scale = 2.1;
  static readonly Yaw = Math.PI + 0.62;
  /** The latest the burst may land in the `open` clip (s): the lid is thrown at ~40 % of it (burstTime). */
  static readonly BurstAt = 31 / 24;

  static rankOf(rarity: string): number { return (VFX.CHEST_RANK as Record<string, number>)[rarity] ?? 0; }
  static colourOf(rarity: string): [number, number, number] {
    return ((VFX.CHEST_COLOR as Record<string, [number, number, number]>)[rarity]) ?? [1, 0.9, 0.55];
  }

  constructor(private readonly scene: THREE.Scene, readonly rarity: string) {
    const L = lib()!;
    const name = `chest_${rarity}`;
    this.rank = ChestModel.rankOf(rarity);
    this.colour = ChestModel.colourOf(rarity);
    this.root = ChestModel.clone(rarity);
    tintLidInside(this.root, rarity, this.lidGlow);
    scene.add(this.root);
    this.root.updateMatrixWorld(true);
    this.lid = findPart(this.root, "lid");
    this.show = new ChestShow(scene, this.root, this.rank, this.colour, ChestModel.Scale);
    this.show.baseScale.copy(this.root.scale);
    // the open clip swings the lid up (fixed at the source in M31); lidOpensUp only guards against an inverted export
    const clips = lidOpensUp(L.clipsOf(`hq:${name}`), rarity);
    this.burstAt = burstTime(clips.find((c) => c.name === "open")?.duration ?? 0, ChestModel.BurstAt);
    this.rig = new AnimRig(this.root, clips);
    this.rig.playLoop(this.rig.has("idle") ? "idle" : "shake", 1, false);
  }

  /** The model at the stage's scale and yaw, its metal lit by the stage's environment. */
  static clone(rarity: string): THREE.Object3D {
    const root = lib()!.clone(`hq:chest_${rarity}`);
    root.scale.multiplyScalar(ChestModel.Scale);
    root.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if (m && m.isMeshStandardMaterial) { m.envMapIntensity = 0.8; m.needsUpdate = true; }
    });
    root.rotation.y = ChestModel.Yaw;              // HQ models face −z; turned so the front and one end both show
    return root;
  }

  /** The bounds the stage frames by (the target rarity's, so the camera never moves while the chest climbs). */
  static bounds(rarity: string): THREE.Box3 {
    const root = ChestModel.clone(rarity);
    root.updateMatrixWorld(true);
    return new THREE.Box3().setFromObject(root);
  }

  /** A part's position in the stage's world (a mythic's shards, for the lightning between them). */
  partWorld(part: string, out: THREE.Vector3): THREE.Vector3 | null {
    const p = findPart(this.root, part);
    return p ? p.getWorldPosition(out) : null;
  }

  dispose(): void {
    this.show.dispose();
    this.rig.reset();
    this.scene.remove(this.root);
  }
}
